import React from 'react';
import '../styles/ikisymbols.css';

const IkiSymbol = ({ name, size = 'md', className = '', ariaLabel }) => {
  const sizeClass = `iki-icon-${size}`;
  const baseClass = `iki-icon iki-${name} iki-soft-shadow ${sizeClass} ${className}`;

  const renderIcon = () => {
    switch (name) {
      case 'lock':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Lock'}>
            <rect className="body" x="20" y="28" width="24" height="20" rx="2" stroke="var(--iki-stroke)" fill="none" />
            <path d="M 24 28 V 20 Q 24 14, 32 14 Q 40 14, 40 20 V 28" stroke="var(--iki-stroke)" fill="none" />
            <circle cx="32" cy="38" r="2" fill="var(--iki-stroke)" />
          </svg>
        );

      case 'mic':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Microphone'}>
            <rect className="base" x="28" y="16" width="8" height="16" rx="4" stroke="var(--iki-stroke)" />
            <path d="M 20 32 Q 20 42, 32 42 Q 44 42, 44 32" stroke="var(--iki-stroke)" fill="none" />
            <line x1="32" y1="42" x2="32" y2="48" stroke="var(--iki-stroke)" />
            <line x1="26" y1="48" x2="38" y2="48" stroke="var(--iki-stroke)" />
          </svg>
        );

      case 'gratitude':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Gratitude'}>
            <defs>
              <radialGradient id="haloGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="var(--iki-ivory)" stopOpacity="0.9"/>
                <stop offset="100%" stopColor="var(--iki-gold)" stopOpacity="0.0"/>
              </radialGradient>
            </defs>
            <circle className="halo" cx="32" cy="32" r="22" fill="url(#haloGrad)" />
            <circle className="sun" cx="32" cy="32" r="10" fill="none" stroke="var(--iki-secondary)" />
            <g stroke="var(--iki-secondary)">
              <line x1="32" y1="14" x2="32" y2="8"/>
              <line x1="32" y1="56" x2="32" y2="50"/>
              <line x1="14" y1="32" x2="8"  y2="32"/>
              <line x1="56" y1="32" x2="50" y2="32"/>
              <line x1="45" y1="19" x2="49" y2="15"/>
              <line x1="19" y1="45" x2="15" y2="49"/>
              <line x1="45" y1="45" x2="49" y2="49"/>
              <line x1="19" y1="19" x2="15" y2="15"/>
            </g>
          </svg>
        );

      case 'calm':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Calm'}>
            <circle className="wave-1" cx="32" cy="32" r="12" stroke="var(--iki-stroke)" fill="none" />
            <circle className="wave-2" cx="32" cy="32" r="18" stroke="var(--iki-mist)" fill="none" />
            <circle className="wave-3" cx="32" cy="32" r="24" stroke="var(--iki-mist)" fill="none" />
          </svg>
        );

      case 'healing':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Cura'}>
            <defs>
              <radialGradient id="healingGlow" cx="50%" cy="45%" r="50%">
                <stop offset="0%" stopColor="var(--iki-gold)" stopOpacity="0.4"/>
                <stop offset="70%" stopColor="var(--iki-turquoise)" stopOpacity="0.15"/>
                <stop offset="100%" stopColor="transparent" stopOpacity="0"/>
              </radialGradient>
            </defs>
            {/* Radiating glow */}
            <circle className="healing-glow" cx="32" cy="32" r="26" fill="url(#healingGlow)" />
            {/* Upward spiral of light - like energy rising */}
            <path className="light-spiral"
              d="M32 48 
                 Q 28 44, 28 40
                 Q 28 36, 32 36
                 Q 36 36, 36 32
                 Q 36 28, 32 28
                 Q 28 28, 28 24
                 Q 28 20, 32 18"
              fill="none"
              stroke="var(--iki-secondary)"
              strokeWidth="1.3"
              strokeLinecap="round"/>
            {/* Open hands receiving/giving */}
            <path className="hand-left"
              d="M20 38 C22 36, 24 35, 26 36 C28 37, 29 38, 30 40"
              fill="none"
              stroke="var(--iki-turquoise)"
              strokeWidth="1.3"
              strokeLinecap="round"
              opacity="0.7"/>
            <path className="hand-right"
              d="M44 38 C42 36, 40 35, 38 36 C36 37, 35 38, 34 40"
              fill="none"
              stroke="var(--iki-turquoise)"
              strokeWidth="1.3"
              strokeLinecap="round"
              opacity="0.7"/>
            {/* Light particles flowing up */}
            <circle className="particle-1" cx="32" cy="44" r="1" fill="var(--iki-gold)" opacity="0.6"/>
            <circle className="particle-2" cx="30" cy="38" r="0.8" fill="var(--iki-gold)" opacity="0.5"/>
            <circle className="particle-3" cx="34" cy="40" r="0.7" fill="var(--iki-gold)" opacity="0.4"/>
          </svg>
        );

      case 'love':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Amor'}>
            <defs>
              <radialGradient id="loveGlow" cx="50%" cy="48%" r="55%">
                <stop offset="0%" stopColor="var(--iki-gold)" stopOpacity="0.5"/>
                <stop offset="60%" stopColor="rgba(217, 203, 163, 0.2)" stopOpacity="0.2"/>
                <stop offset="100%" stopColor="transparent" stopOpacity="0"/>
              </radialGradient>
            </defs>
            {/* Golden inner glow */}
            <circle className="love-glow" cx="32" cy="32" r="28" fill="url(#loveGlow)" />
            {/* Open heart - hand-drawn, organic feel */}
            <path className="heartline" 
              d="M32 47
                 C 21 38, 15.5 31, 15 24
                 C 14.8 19.5, 18 15.5, 23 15.5
                 C 26.5 15.5, 29.5 17.5, 31.5 20
                 C 32 19.3, 32.5 18.5, 33 17.8
                 C 34.5 16, 37 15.5, 40.5 15.5
                 C 45.5 15.5, 49 19.5, 49 24
                 C 48.5 31, 43 38, 32 47"
              fill="none"
              stroke="var(--iki-secondary)"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"/>
            {/* Radiating pulse lines - heartbeat */}
            <path className="pulse-left" 
              d="M18 32 Q16 32, 14 32"
              stroke="var(--iki-gold)"
              strokeWidth="1.2"
              strokeLinecap="round"
              opacity="0.4"/>
            <path className="pulse-right" 
              d="M46 32 Q48 32, 50 32"
              stroke="var(--iki-gold)"
              strokeWidth="1.2"
              strokeLinecap="round"
              opacity="0.4"/>
          </svg>
        );

      case 'focus':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Focus'}>
            {/* Pulsing halo rings - expanding clarity */}
            <circle className="halo-outer" cx="32" cy="32" r="24" 
              stroke="var(--iki-mist)" 
              strokeWidth="1"
              fill="none" 
              opacity="0.3"/>
            <circle className="halo-mid" cx="32" cy="32" r="18" 
              stroke="var(--iki-turquoise)" 
              strokeWidth="1.2"
              fill="none" 
              opacity="0.4"/>
            <circle className="halo-inner" cx="32" cy="32" r="12" 
              stroke="var(--iki-secondary)" 
              strokeWidth="1.3"
              fill="none" 
              opacity="0.6"/>
            {/* Luminous center dot - pure alignment */}
            <circle className="core-dot" cx="32" cy="32" r="3" 
              fill="var(--iki-gold)" 
              opacity="0.9"/>
            <circle className="core-glow" cx="32" cy="32" r="5" 
              fill="var(--iki-gold)" 
              opacity="0.2"/>
          </svg>
        );

      case 'growth':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Growth'}>
            <defs>
              <linearGradient id="growthGradient" x1="50%" y1="100%" x2="50%" y2="0%">
                <stop offset="0%" stopColor="var(--iki-turquoise)" stopOpacity="0.8"/>
                <stop offset="100%" stopColor="var(--iki-gold)" stopOpacity="0.9"/>
              </linearGradient>
            </defs>
            {/* Sprouting form - seed of life inspired */}
            {/* Left curve */}
            <path className="sprout-left"
              d="M32 48 
                 Q28 44, 26 38
                 Q24 32, 24 26
                 Q24 22, 26 20"
              fill="none"
              stroke="url(#growthGradient)"
              strokeWidth="1.3"
              strokeLinecap="round"/>
            {/* Right curve */}
            <path className="sprout-right"
              d="M32 48 
                 Q36 44, 38 38
                 Q40 32, 40 26
                 Q40 22, 38 20"
              fill="none"
              stroke="url(#growthGradient)"
              strokeWidth="1.3"
              strokeLinecap="round"/>
            {/* Center stem */}
            <line className="stem" x1="32" y1="48" x2="32" y2="18" 
              stroke="var(--iki-secondary)" 
              strokeWidth="1.3" 
              strokeLinecap="round"/>
            {/* Emerging buds */}
            <circle className="bud-1" cx="26" cy="24" r="1.5" fill="var(--iki-gold)" opacity="0.6"/>
            <circle className="bud-2" cx="38" cy="24" r="1.5" fill="var(--iki-gold)" opacity="0.6"/>
            <circle className="bud-3" cx="32" cy="18" r="1.8" fill="var(--iki-gold)" opacity="0.8"/>
          </svg>
        );

      case 'abundance':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Abundância'}>
            <defs>
              <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="var(--iki-turquoise)" stopOpacity="0.8"/>
                <stop offset="50%" stopColor="var(--iki-gold)" stopOpacity="0.9"/>
                <stop offset="100%" stopColor="var(--iki-turquoise)" stopOpacity="0.8"/>
              </linearGradient>
            </defs>
            {/* Infinity loop - continuous giving and receiving */}
            <path className="infinity-flow"
              d="M18 32 
                 C18 26, 22 22, 26 22
                 C30 22, 32 26, 32 32
                 C32 26, 34 22, 38 22
                 C42 22, 46 26, 46 32
                 C46 38, 42 42, 38 42
                 C34 42, 32 38, 32 32
                 C32 38, 30 42, 26 42
                 C22 42, 18 38, 18 32 Z"
              fill="none"
              stroke="url(#flowGradient)"
              strokeWidth="1.3"
              strokeLinecap="round"/>
            {/* Flow dots moving along the path */}
            <circle className="flow-dot-1" cx="26" cy="27" r="1.2" fill="var(--iki-gold)" opacity="0.7"/>
            <circle className="flow-dot-2" cx="38" cy="37" r="1" fill="var(--iki-gold)" opacity="0.6"/>
            <circle className="flow-dot-3" cx="32" cy="32" r="0.8" fill="var(--iki-ivory)" opacity="0.5"/>
          </svg>
        );

      case 'presence':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Presence'}>
            <circle className="dot" cx="32" cy="32" r="6" fill="var(--iki-stroke)" />
            <circle cx="32" cy="32" r="14" stroke="var(--iki-stroke)" fill="none" opacity="0.5" />
            <circle cx="32" cy="32" r="22" stroke="var(--iki-mist)" fill="none" opacity="0.3" />
          </svg>
        );

      case 'journal':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Journal'}>
            <rect x="22" y="16" width="20" height="32" rx="1" stroke="var(--iki-stroke)" fill="none" />
            <line x1="28" y1="24" x2="36" y2="24" stroke="var(--iki-stroke)" opacity="0.5" />
            <line x1="28" y1="30" x2="36" y2="30" stroke="var(--iki-stroke)" opacity="0.5" />
            <line x1="28" y1="36" x2="34" y2="36" stroke="var(--iki-stroke)" opacity="0.5" />
            <path className="feather" d="M 38 14 L 38 22 M 36 16 L 40 16" stroke="var(--iki-secondary)" />
          </svg>
        );

      case 'meditate':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Meditar'}>
            <path className="silhouette" d="M16 46 C24 42, 40 42, 48 46" fill="none" stroke="var(--iki-stroke)" />
            <path className="silhouette iki-anim-breathe"
              d="M32 18
                 C28 18, 26 22, 26 24
                 C26 28, 30 30, 32 32
                 C34 30, 38 28, 38 24
                 C38 22, 36 18, 32 18
                 M24 38 C28 36, 36 36, 40 38"
              fill="none"
              stroke="var(--iki-stroke)"
              strokeLinecap="round"
              strokeLinejoin="round" />
          </svg>
        );

      case 'microdose':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Microdose'}>
            <circle cx="28" cy="28" r="2" className="particles" fill="var(--iki-secondary)" opacity="0.6" />
            <circle cx="36" cy="26" r="1.5" className="particles" fill="var(--iki-secondary)" opacity="0.5" />
            <circle cx="32" cy="34" r="2.5" className="particles" fill="var(--iki-stroke)" opacity="0.7" />
            <circle cx="26" cy="36" r="1" className="particles" fill="var(--iki-mist)" />
            <circle cx="38" cy="32" r="1.8" className="particles" fill="var(--iki-mist)" opacity="0.6" />
          </svg>
        );

      case 'sparkles':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Sparkles'}>
            <path className="spark" d="M 24 28 L 26 32 L 24 36 L 22 32 Z" fill="var(--iki-gold)" />
            <path className="spark" d="M 36 20 L 38 24 L 36 28 L 34 24 Z" fill="var(--iki-gold)" opacity="0.7" />
            <path className="spark" d="M 40 36 L 42 40 L 40 44 L 38 40 Z" fill="var(--iki-gold)" opacity="0.8" />
          </svg>
        );

      case 'shield':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Shield'}>
            <path className="outline" d="M 32 16 L 44 20 L 44 34 Q 44 44, 32 48 Q 20 44, 20 34 L 20 20 Z" 
              stroke="var(--iki-stroke)" fill="none" />
            <path d="M 28 32 L 30 34 L 36 28" stroke="var(--iki-secondary)" fill="none" />
          </svg>
        );

      case 'circle':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Comunidade'}>
            <circle className="ring iki-anim-rotate" cx="32" cy="32" r="20" fill="none" stroke="var(--iki-stroke)" />
            <circle cx="32" cy="12" r="2" fill="var(--iki-secondary)" />
            <circle cx="46" cy="18" r="2" fill="var(--iki-secondary)" />
            <circle cx="52" cy="32" r="2" fill="var(--iki-secondary)" />
            <circle cx="46" cy="46" r="2" fill="var(--iki-secondary)" />
            <circle cx="32" cy="52" r="2" fill="var(--iki-secondary)" />
            <circle cx="18" cy="46" r="2" fill="var(--iki-secondary)" />
            <circle cx="12" cy="32" r="2" fill="var(--iki-secondary)" />
            <circle cx="18" cy="18" r="2" fill="var(--iki-secondary)" />
          </svg>
        );

      case 'synch':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Sincronicidades'}>
            <circle className="vesica iki-anim-breathe" cx="26" cy="32" r="16" fill="none" stroke="var(--iki-stroke)" />
            <circle className="vesica iki-anim-breathe" cx="38" cy="32" r="16" fill="none" stroke="var(--iki-stroke)" 
              style={{ animationDelay: '0.3s' }} />
            <circle cx="32" cy="32" r="1.6" fill="var(--iki-secondary)" />
            <circle cx="29" cy="32" r="1" fill="var(--iki-secondary)" opacity=".6" />
            <circle cx="35" cy="32" r="1" fill="var(--iki-secondary)" opacity=".6" />
          </svg>
        );

      case 'insight':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Insight'}>
            <circle cx="32" cy="32" r="14" stroke="var(--iki-stroke)" fill="none" />
            <path className="brain" d="M 28 28 Q 32 26, 36 28 M 28 32 L 36 32 M 28 36 Q 32 38, 36 36" 
              stroke="var(--iki-stroke)" fill="none" />
          </svg>
        );

      case 'energy':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Energia'}>
            <polygon className="bolt" points="30,10 18,36 30,36 26,54 46,26 34,26 38,10"
              fill="none" stroke="var(--iki-secondary)" strokeLinejoin="round" />
          </svg>
        );

      case 'clarity':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Clareza'}>
            <circle className="ring" cx="32" cy="32" r="20" stroke="var(--iki-stroke)" fill="none" />
            <polygon className="triangle" points="32,18 22,42 42,42"
              fill="none" stroke="var(--iki-secondary)" strokeLinejoin="round" />
          </svg>
        );

      case 'record':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Record'}>
            <circle className="mic" cx="32" cy="32" r="8" stroke="var(--iki-stroke)" fill="none" />
            <line x1="32" y1="40" x2="32" y2="48" stroke="var(--iki-stroke)" />
            <line x1="26" y1="48" x2="38" y2="48" stroke="var(--iki-stroke)" />
          </svg>
        );

      case 'listen':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Listen'}>
            <path className="wave" d="M 20 32 Q 26 24, 32 32 Q 38 40, 44 32" 
              stroke="var(--iki-stroke)" fill="none" />
            <circle cx="32" cy="32" r="3" fill="var(--iki-secondary)" />
          </svg>
        );

      case 'sadness':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Tristeza'}>
            <path className="drop iki-anim-breathe" 
              d="M32 16 C28 24, 26 30, 26 36 C26 42, 28 48, 32 48 C36 48, 38 42, 38 36 C38 30, 36 24, 32 16 Z"
              fill="none"
              stroke="var(--iki-stroke)"
              strokeLinecap="round"/>
            <circle cx="32" cy="36" r="3" fill="var(--iki-mist)" opacity="0.5" />
          </svg>
        );

      case 'joy':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Alegria'}>
            <path className="star iki-anim-shimmer" 
              d="M32 12 L35 28 L48 28 L37 36 L42 52 L32 44 L22 52 L27 36 L16 28 L29 28 Z"
              fill="none"
              stroke="var(--iki-secondary)"
              strokeLinecap="round"
              strokeLinejoin="round"/>
            <circle className="glow" cx="32" cy="32" r="20" fill="var(--iki-glow)" opacity="0.3" />
          </svg>
        );

      case 'rocket':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Lançamento'}>
            <path className="body" 
              d="M32 12 L38 28 L34 44 L30 44 L26 28 Z"
              fill="none"
              stroke="var(--iki-stroke)"
              strokeLinecap="round"
              strokeLinejoin="round"/>
            <line x1="26" y1="32" x2="22" y2="36" stroke="var(--iki-stroke)" />
            <line x1="38" y1="32" x2="42" y2="36" stroke="var(--iki-stroke)" />
            <path className="flame iki-anim-flicker" 
              d="M28 44 L30 50 L32 48 L34 50 L36 44"
              stroke="var(--iki-secondary)"
              fill="none"
              strokeLinecap="round"/>
            <circle cx="32" cy="20" r="2" fill="var(--iki-secondary)" />
          </svg>
        );

      case 'save':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Guardar'}>
            <rect x="20" y="16" width="24" height="32" rx="2" stroke="var(--iki-stroke)" fill="none" />
            <line x1="20" y1="24" x2="44" y2="24" stroke="var(--iki-stroke)" />
            <rect x="28" y="16" width="8" height="8" fill="var(--iki-secondary)" />
            <circle cx="32" cy="36" r="4" stroke="var(--iki-stroke)" fill="none" />
          </svg>
        );

      case 'delete':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Eliminar'}>
            <path d="M24 22 L24 46 L40 46 L40 22" stroke="var(--iki-stroke)" fill="none" />
            <line x1="18" y1="22" x2="46" y2="22" stroke="var(--iki-stroke)" />
            <path d="M28 18 L28 22 M36 18 L36 22" stroke="var(--iki-stroke)" />
            <line x1="28" y1="28" x2="28" y2="40" stroke="var(--iki-mist)" opacity="0.6" />
            <line x1="32" y1="28" x2="32" y2="40" stroke="var(--iki-mist)" opacity="0.6" />
            <line x1="36" y1="28" x2="36" y2="40" stroke="var(--iki-mist)" opacity="0.6" />
          </svg>
        );

      case 'chart':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Gráfico'}>
            <rect x="18" y="36" width="8" height="12" stroke="var(--iki-stroke)" fill="none" />
            <rect x="28" y="28" width="8" height="20" stroke="var(--iki-stroke)" fill="none" />
            <rect x="38" y="20" width="8" height="28" stroke="var(--iki-stroke)" fill="none" />
            <line x1="14" y1="52" x2="50" y2="52" stroke="var(--iki-mist)" />
          </svg>
        );

      case 'settings':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Definições'}>
            <circle cx="32" cy="32" r="6" stroke="var(--iki-stroke)" fill="none" />
            <circle className="gear iki-anim-rotate-slow" cx="32" cy="32" r="16" stroke="var(--iki-stroke)" fill="none" strokeDasharray="2 6" />
            <line x1="32" y1="12" x2="32" y2="18" stroke="var(--iki-stroke)" />
            <line x1="32" y1="46" x2="32" y2="52" stroke="var(--iki-stroke)" />
            <line x1="12" y1="32" x2="18" y2="32" stroke="var(--iki-stroke)" />
            <line x1="46" y1="32" x2="52" y2="32" stroke="var(--iki-stroke)" />
          </svg>
        );

      case 'notification':
      case 'bell':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Notificações'}>
            <path className="bell" 
              d="M24 28 C24 22, 26 18, 32 18 C38 18, 40 22, 40 28 L40 38 C40 42, 44 44, 44 44 L20 44 C20 44, 24 42, 24 38 Z"
              stroke="var(--iki-stroke)"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"/>
            <path d="M28 44 C28 46, 30 48, 32 48 C34 48, 36 46, 36 44" stroke="var(--iki-stroke)" fill="none" />
            <line x1="32" y1="14" x2="32" y2="18" stroke="var(--iki-stroke)" />
          </svg>
        );

      case 'play':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Reproduzir'}>
            <circle cx="32" cy="32" r="20" stroke="var(--iki-stroke)" fill="none" />
            <path d="M28 22 L28 42 L44 32 Z" fill="var(--iki-secondary)" stroke="none" />
          </svg>
        );

      case 'pause':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Pausar'}>
            <circle cx="32" cy="32" r="20" stroke="var(--iki-stroke)" fill="none" />
            <rect x="26" y="24" width="4" height="16" fill="var(--iki-secondary)" />
            <rect x="34" y="24" width="4" height="16" fill="var(--iki-secondary)" />
          </svg>
        );

      case 'stop':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Parar'}>
            <circle cx="32" cy="32" r="20" stroke="var(--iki-stroke)" fill="none" />
            <rect x="24" y="24" width="16" height="16" fill="var(--iki-secondary)" />
          </svg>
        );

      case 'edit':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Editar'}>
            <path d="M16 48 L22 42 L42 22 L48 28 L28 48 L16 48 Z" 
              stroke="var(--iki-stroke)" 
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round" />
            <line x1="38" y1="26" x2="44" y2="32" stroke="var(--iki-secondary)" />
            <path d="M16 48 L22 46" stroke="var(--iki-mist)" />
          </svg>
        );

      case 'share':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Partilhar'}>
            <circle cx="20" cy="32" r="6" stroke="var(--iki-stroke)" fill="none" />
            <circle cx="44" cy="20" r="6" stroke="var(--iki-stroke)" fill="none" />
            <circle cx="44" cy="44" r="6" stroke="var(--iki-stroke)" fill="none" />
            <line x1="26" y1="30" x2="38" y2="22" stroke="var(--iki-stroke)" />
            <line x1="26" y1="34" x2="38" y2="42" stroke="var(--iki-stroke)" />
          </svg>
        );

      case 'download':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Transferir'}>
            <line x1="32" y1="16" x2="32" y2="42" stroke="var(--iki-stroke)" />
            <path d="M24 34 L32 42 L40 34" 
              stroke="var(--iki-stroke)" 
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round" />
            <line x1="18" y1="48" x2="46" y2="48" stroke="var(--iki-stroke)" strokeWidth="2" />
          </svg>
        );

      case 'info':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Informação'}>
            <circle cx="32" cy="32" r="20" stroke="var(--iki-stroke)" fill="none" />
            <circle cx="32" cy="24" r="2" fill="var(--iki-stroke)" />
            <line x1="32" y1="30" x2="32" y2="42" stroke="var(--iki-stroke)" strokeWidth="2" />
          </svg>
        );

      case 'shield-lock':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Proteção'}>
            <path d="M 32 16 L 44 20 L 44 34 Q 44 44, 32 48 Q 20 44, 20 34 L 20 20 Z" 
              stroke="var(--iki-stroke)" fill="none" />
            <rect x="28" y="30" width="8" height="6" rx="1" stroke="var(--iki-secondary)" fill="none" />
            <path d="M 29 30 V 28 Q 29 26, 32 26 Q 35 26, 35 28 V 30" 
              stroke="var(--iki-secondary)" 
              fill="none" />
          </svg>
        );

      case 'snooze':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Adiar'}>
            <circle cx="32" cy="32" r="18" stroke="var(--iki-stroke)" fill="none" />
            <line x1="32" y1="20" x2="32" y2="32" stroke="var(--iki-stroke)" />
            <line x1="32" y1="32" x2="38" y2="36" stroke="var(--iki-stroke)" />
            <path className="zzz" d="M42 16 L48 16 L42 22 L48 22" 
              stroke="var(--iki-mist)" 
              fill="none"
              strokeLinecap="round" />
            <path className="zzz" d="M44 8 L50 8 L44 14 L50 14" 
              stroke="var(--iki-mist)" 
              fill="none"
              strokeLinecap="round"
              opacity="0.6" />
          </svg>
        );

      case 'chevron-right':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Chevron Right'}>
            <path d="M24 16 L40 32 L24 48" 
              stroke="var(--iki-stroke)" 
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round" />
          </svg>
        );

      // === FOCUS AREA ICONS - Minimalist, Abstract, Geometric ===
      
      // Rest/Arc - Sleep & Rest (arc suggesting pause/rest state)
      case 'rest-arc':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Rest'}>
            <path d="M20 32 Q32 20, 44 32" 
              stroke="var(--iki-stroke)" 
              strokeWidth="1.3"
              fill="none"
              strokeLinecap="round" />
            <circle cx="32" cy="38" r="2" fill="var(--iki-stroke)" opacity="0.6" />
          </svg>
        );

      // Axis - Confidence (vertical line with horizontal stabilizers)
      case 'axis':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Axis'}>
            <line x1="32" y1="16" x2="32" y2="48" 
              stroke="var(--iki-stroke)" 
              strokeWidth="1.3"
              strokeLinecap="round" />
            <line x1="24" y1="32" x2="40" y2="32" 
              stroke="var(--iki-stroke)" 
              strokeWidth="1.3"
              strokeLinecap="round" />
            <circle cx="32" cy="32" r="3" fill="none" stroke="var(--iki-stroke)" strokeWidth="1" opacity="0.5" />
          </svg>
        );

      // Contain - Grief (open container/vessel shape - space for holding)
      case 'contain':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Contain'}>
            <path d="M20 24 L20 44 Q20 48, 24 48 L40 48 Q44 48, 44 44 L44 24" 
              stroke="var(--iki-stroke)" 
              strokeWidth="1.3"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round" />
          </svg>
        );

      // Tension - Stress (parallel lines with compression)
      case 'tension':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Tension'}>
            <line x1="20" y1="24" x2="20" y2="40" stroke="var(--iki-stroke)" strokeWidth="1.3" strokeLinecap="round" />
            <line x1="28" y1="28" x2="28" y2="36" stroke="var(--iki-stroke)" strokeWidth="1.3" strokeLinecap="round" />
            <line x1="36" y1="28" x2="36" y2="36" stroke="var(--iki-stroke)" strokeWidth="1.3" strokeLinecap="round" />
            <line x1="44" y1="24" x2="44" y2="40" stroke="var(--iki-stroke)" strokeWidth="1.3" strokeLinecap="round" />
            <path d="M24 32 L28 32 M36 32 L40 32" stroke="var(--iki-stroke)" strokeWidth="1" opacity="0.5" />
          </svg>
        );

      // Balance - Emotional Regulation (scales/equilibrium)
      case 'balance':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Balance'}>
            <line x1="32" y1="20" x2="32" y2="44" stroke="var(--iki-stroke)" strokeWidth="1.3" strokeLinecap="round" />
            <line x1="18" y1="28" x2="46" y2="28" stroke="var(--iki-stroke)" strokeWidth="1.3" strokeLinecap="round" />
            <circle cx="18" cy="28" r="2" fill="none" stroke="var(--iki-stroke)" strokeWidth="1" />
            <circle cx="46" cy="28" r="2" fill="none" stroke="var(--iki-stroke)" strokeWidth="1" />
            <path d="M26 44 L38 44" stroke="var(--iki-stroke)" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        );

      // Restore - Recovery (returning arc, restoration path)
      case 'restore':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Restore'}>
            <path d="M44 28 Q44 20, 32 20 Q20 20, 20 32 Q20 44, 32 44 Q40 44, 44 40" 
              stroke="var(--iki-stroke)" 
              strokeWidth="1.3"
              fill="none"
              strokeLinecap="round" />
            <path d="M44 22 L44 28 L38 28" 
              stroke="var(--iki-stroke)" 
              strokeWidth="1.3"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round" />
          </svg>
        );

      // Center - General Wellbeing (concentric alignment)
      case 'center':
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Center'}>
            <circle cx="32" cy="32" r="16" stroke="var(--iki-stroke)" strokeWidth="1.3" fill="none" />
            <circle cx="32" cy="32" r="8" stroke="var(--iki-stroke)" strokeWidth="1" fill="none" opacity="0.6" />
            <circle cx="32" cy="32" r="2" fill="var(--iki-stroke)" />
          </svg>
        );

      default:
        return (
          <svg className={baseClass} viewBox="0 0 64 64" role="img" aria-label={ariaLabel || 'Icon'}>
            <circle cx="32" cy="32" r="16" stroke="var(--iki-stroke)" fill="none" />
          </svg>
        );
    }
  };

  return renderIcon();
};

export default IkiSymbol;
